import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { ModuleState } from '@redux/modules/module'

import { BrandThemeRequestOptions } from '@models/brandTheme'

const watchEntityKeys = ['brandThemes']

type BrandThemeFilters = {
  brand_id?: number,
  string?: string,
}

type UseBrandThemesOptions = {
  entityKey?: string,
  filters?: BrandThemeFilters,
  performHttpRequests?: boolean,
  requestOptions?: BrandThemeRequestOptions,
}

function useBrandThemes(options: UseBrandThemesOptions = {}) {
  const { entityKey, filters, requestOptions } = options
  const { brand_id: filterBrandId, string } = filters || {}

  const {
    updatedEntities: { brandThemes: brandThemesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const brandThemes = useSelector(reduxState => reduxState.entities.brandThemes)

  const filteredBrandThemes = useMemo(() => {
    const filtered = Object.values(brandThemes).filter((brandTheme) => {
      const { brand_id, id, title } = brandTheme

      const matchBrand = matchFilterNumber(brand_id, filterBrandId)
      const matchId = id.toString() === string
      const matchTitle = matchFilterString(title, string)
      const matchString = matchId || matchTitle

      return matchBrand && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [brandThemesUpdatedAt, JSON.stringify(filters)])

  const hasFilteredBrandThemes = !!filteredBrandThemes.length
  const filteredBrandThemesCount = filteredBrandThemes.length

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandThemesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrandThemes } = useReduxAction(
    'brandThemes',
    'loadBrandThemes',
    {
      entityKey,
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandThemes,
    filteredBrandThemesCount,
    hasFilteredBrandThemes,
    loading: loadingBrandThemes,
  }
}

export default useBrandThemes
